"use client";

import { useAppStore } from "@/store/useAppStore";
import { badgeSystem } from "@/utils/badgeSystem";
import React, { useState } from "react";
import { FeedbackModal } from "./modals/FeedbackModal";
import { updateMintedBadge } from "@/lib/badges/updateMintBadge";
import { updateUserScore } from "@/lib/user/updateUserScore";
import { useAuth } from "@/hooks/useAuth";
import { addChallengeActivity } from "@/lib/activity/addActivityToDB";
import { toast } from "react-toastify";

export const BadgeEarnedModal = () => {
  const {
    isMinting,
    newBadge,
    showBadgeModal,
    mintedBadges,
    setShowBadgeModal,
    setNewBadge,
    setIsMinting,
    setMintedBadges,
    updateUserPointsScore,
  } = useAppStore();

  const { user } = useAuth();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [mintTxHash, setMintTxHash] = useState("");

  const handleMintBadge = async () => {
    try {
      if (!newBadge) return;

      setIsMinting(true);
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const nftMetadata = {
        name: newBadge.name,
        description: newBadge.description,
        image: `https://probabl.app/badges/${newBadge.name
          .toLowerCase()
          .replace(/\s+/g, "-")}.png`,
        attributes: [
          { trait_type: "Rarity", value: newBadge.rarity },
          { trait_type: "Points", value: newBadge.points },
          { trait_type: "Power", value: newBadge.nftMetadata.power },
          { trait_type: "Background", value: newBadge.nftMetadata.background },
          { trait_type: "Trait", value: newBadge.nftMetadata.trait },
          { trait_type: "Earned Date", value: new Date().toISOString() },
        ],
        external_url: "https://probabl.app",
        background_color: "000000",
      };

      const badgeId = Object.entries(badgeSystem).find(
        ([_, badge]) => badge === newBadge
      )?.[0];
      const generateTxHash = () => {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return (
          "0x" +
          Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
            ""
          )
        );
      };
      const txHash = generateTxHash();
      updateUserPointsScore(newBadge.points);
      user?.id && (await updateUserScore(user.id, newBadge.points));
      await addChallengeActivity(newBadge.points);

      if (badgeId) {
        await updateMintedBadge(badgeId, txHash);
        setMintedBadges([...mintedBadges, badgeId]);
      }

      setIsMinting(false);
      setMintTxHash(txHash);
      setShowSuccessModal(true);
    } catch (error) {
      console.log("Error handling mint badge: ", error);
      toast.error("Error handling mint badge");
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setShowBadgeModal(false);
    setNewBadge(null);
    setMintTxHash("");
  };
  if (!showBadgeModal && !newBadge) {
    return null;
  }
  return (
    <>
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-brand-teal/20 to-brand-amber/20 backdrop-blur-xl rounded-3xl p-8 border border-brand-teal/30 max-w-md w-full text-center">
          {!isMinting ? (
            <>
              {newBadge && (
                <>
                  <div className="text-6xl mb-4 animate-bounce">
                    {newBadge.emoji}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    🎉 Badge Earned!
                  </h3>
                  <h4 className="text-xl text-brand-accent mb-4">
                    {newBadge.name}
                  </h4>
                  <p className="text-gray-300 mb-4">{newBadge.description}</p>

                  <div className="bg-white/10 rounded-xl p-4 mb-6">
                    <div className="text-sm text-gray-300 space-y-2">
                      <div>
                        <strong>Rarity:</strong>{" "}
                        <span
                          className={`font-bold ${
                            newBadge.rarity === "Common"
                              ? "text-gray-400"
                              : newBadge.rarity === "Uncommon"
                              ? "text-green-400"
                              : newBadge.rarity === "Rare"
                              ? "text-yellow-400"
                              : newBadge.rarity === "Legendary"
                              ? "text-brand-light"
                              : "text-brand-accent"
                          }`}
                        >
                          {newBadge.rarity}
                        </span>
                      </div>
                      <div>
                        <strong>Digital Collectible:</strong> No guaranteed
                        monetary value
                      </div>
                      <div>
                        <strong>Bonus Points:</strong>{" "}
                        <span className="text-green-400 font-bold">
                          +{newBadge.points}
                        </span>
                      </div>
                      <div>
                        <strong>Power Level:</strong>{" "}
                        <span className="text-blue-400">
                          {newBadge.nftMetadata.power}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="space-y-3">
                <button
                  onClick={handleMintBadge}
                  className="w-full bg-gradient-to-r from-brand-teal to-brand-amber text-white px-8 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
                >
                  Mint NFT Badge 🎯
                </button>
                <button
                  onClick={() => {
                    setShowBadgeModal(false);
                    setNewBadge(null);
                  }}
                  className="w-full text-gray-400 hover:text-white text-sm"
                >
                  Skip for now
                </button>
              </div>
            </>
          ) : (
            <div className="py-8">
              <div className="text-6xl mb-6 animate-spin">⚡</div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Minting Your NFT...
              </h3>
              <div className="mb-6">
                <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                  <div
                    className="h-2 bg-gradient-to-r from-brand-teal to-brand-amber rounded-full animate-pulse"
                    style={{ width: "75%" }}
                  ></div>
                </div>
                <p className="text-sm text-gray-400">
                  Simulating blockchain transaction...
                </p>
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                <p>🔗 Connecting to blockchain...</p>
                <p>📝 Creating metadata...</p>
                <p>🎨 Generating NFT artwork...</p>
                <p>⛓️ Confirming transaction...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {showSuccessModal && newBadge && (
        <FeedbackModal
          type="success"
          handleContinue={handleSuccessModalClose}
          showConfetti={true}
        >
          <div className="text-6xl mb-4">🎯</div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Badge Minted Successfully!
          </h2>
          <p className="text-gray-200 mb-6 leading-relaxed">
            Your{" "}
            <span className="text-brand-accent font-semibold">
              {newBadge.name}
            </span>{" "}
            badge is now in your collection!
          </p>

          <div className="bg-green-500/20 rounded-lg p-4 mb-6 border border-green-500/30">
            <div className="text-sm text-green-300 space-y-2">
              <div className="font-semibold">✅ Transaction Complete</div>
              <div className="break-all text-xs">
                <strong>Hash:</strong> {mintTxHash.slice(0, 10)}...
                {mintTxHash.slice(-8)}
              </div>
              <div>
                <strong>Power Level:</strong>{" "}
                <span className="text-blue-300">
                  {newBadge.nftMetadata.power}
                </span>
              </div>
              <div>
                <strong>Bonus Points:</strong>{" "}
                <span className="text-yellow-300">+{newBadge.points}</span>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-400">
            🏆 Check your collection to view your new NFT badge!
          </div>
        </FeedbackModal>
      )}
    </>
  );
};
